import type { Lang, LorsCopy } from "./translations";

export type Oct28Headline = { headline: string; subhead: string };

export const oct28Headlines: Oct28Headline[] = [
  {
    headline: "📌 You did the right thing. You made a will. It's just not the whole thing.",
    subhead:
      "Learn where a will stops and a trust starts — and why California homeowners need both.",
  },
  {
    headline: "📌 Everyone said \"get a will.\" Nobody mentioned it still goes through probate.",
    subhead:
      "A free workshop on the part of the plan most families are missing.",
  },
  {
    headline: "📌 Your home is the biggest thing you'll ever pass down. Probate is the biggest thing standing in the way.",
    subhead:
      "A one-hour workshop on keeping your family's most valuable asset out of court.",
  },
  {
    headline: "📌 Two neighbors. Same street. Same home value. One family inherits the house. The other inherits a case number.",
    subhead:
      "The difference is one document. Learn what it is and how to get it in place.",
  },
  {
    headline: "📌 You spent decades paying off the mortgage. The plan for what happens next takes one afternoon.",
    subhead:
      "A free workshop on the documents every California homeowner should have.",
  },
  {
    headline: "📌 Without a plan, your family doesn't lose the house. They lose a year trying to get it.",
    subhead:
      "Learn what California homeowners need in place before it becomes someone else's problem.",
  },
  {
    headline: "📌 Probate takes over a year in California. Your family pays every month it drags on.",
    subhead:
      "One hour to learn how a trust keeps your home out of court entirely.",
  },
  {
    headline: "📌 This is the thing on your list that actually matters, and it's easier than you think",
    subhead:
      "A free in-person workshop on wills, trusts, and what California homeowners need to know.",
  },
  {
    headline: "📌 A will doesn't skip the courthouse. It's the reason your family ends up there.",
    subhead:
      "Find out what actually protects your home in California — and what just delays the inevitable.",
  },
  {
    headline: "📌 Come with questions. Leave with a plan your family will thank you for.",
    subhead:
      "A free hands-on workshop with Ruby Steinbrecher — no jargon, no pressure, just real answers.",
  },
];

const baseEn = {
  eyebrow: "Free Estate Planning Workshop",
  headline: oct28Headlines[0].headline,
  headlineBreakBefore: "",
  subhead: oct28Headlines[0].subhead,
  subheadLabel: "A note from Ruby:",
  learnTitle: "What Most Families Don't Realize Until It's Too Late",
  learnItems: [
    "How wills, living trusts, and powers of attorney each work in California",
    "How a properly funded living trust can help a family pass assets without probate court",
    "What California's probate process generally looks like — timelines, court involvement, and typical costs",
    "Practical first steps for putting an estate plan in place, and the questions to ask before you do",
  ],
  quote:
    "Ruby Steinbrecher was fantastic. The family trust workshop was clear, informative, and extremely helpful for getting started with estate planning.",
  quoteAttribution: "Shawn H., Workshop Attendee",
  formDate: { short: "Wed, October 28th", long: "Wednesday, October 28th" },
  formTime: "4:00–5:00 PM",
  formVenueLine1: "Hilton Garden Inn",
  formVenueLine2: "417 Aviation Blvd, Santa Rosa, CA 95403",
  formTitle: "Reserve Your Seat",
  formSubtitle: "Free to attend. Limited seating available.",
  formFirstName: "First name",
  formLastName: "Last name",
  formEmail: "Email",
  formPhone: "Phone",
  formSmsConsent:
    "I consent to receive text messages from the Law Office of Ruby Steinbrecher about this event, and occasional marketing messages. Message & data rates may apply. Text HELP for assistance, reply STOP to opt out.",
  formEnewsConsent:
    "I'd like to receive occasional email updates from the Law Office of Ruby Steinbrecher.",
  formPrivacy: "Privacy Policy",
  formSubmit: "Reserve My Seat",
  formSubmitting: "Reserving your seat…",
  formTrust: "⭐ Trusted by local families | 81 Five-Star Reviews",
  getDirections: "Get Directions",
  footerCopyright: "© 2026 Ruby Steinbrecher",
  footerPrivacy: "Privacy Policy",
  testimonialsTitle: "What Families Are Saying",
  aboutTitle: "Meet Ruby Steinbrecher",
  aboutRole: "Attorney",
  aboutBody: [
    "I have practiced law since 2007, first working at a medium-sized firm in San Francisco handling civil litigation, and then moving into estate planning, small business and contract law since 2010. I value people, the planet, and prosperity that does not adversely affect the former.",
    "Prior to entering the legal field, I had a variety of business and life experience that has only enhanced my ability to serve clients: Road Manager for Ken Kesey\'s bus trip to Great Britain in 1999, Human Resources at a tech startup in 2001, recognized for my work as a Case Manager at Casey Family Programs with disadvantaged youth in the foster care system in Oakland and the wider San Francisco-Bay Area in the early 2000\'s.",
    "Currently, I live in Sebastopol with my daughter, husband, and a menagerie of pets, where I enjoy gardening as it keeps me grounded and enjoying the beautiful weather.",
  ],
  registrationTitle: "Ready to Secure Your Family's Future?",
  registrationSub: "Free to attend. Limited seating available.",
  registrationCta: "Save My Seat",
  registrationFoot:
    "No pressure. Just clear information so you can make the right decision for your family.",
  errorTitle: "Something went wrong",
  errorBody:
    "We couldn't reserve your seat. Please try again or call our office.",
  validation: {
    firstName: "First name is required",
    lastName: "Last name is required",
    email: "Enter a valid email",
    phone: "Enter a valid phone number",
  },
  timeAgo: {
    monthAgo: "a month ago",
    monthsAgo: (n: number) => `${n} months ago`,
    yearAgo: "a year ago",
  },
};

export const lorsContentOct28 = { en: baseEn, es: baseEn } as unknown as Record<Lang, LorsCopy>;

export const OCT28_DIRECTIONS_URL =
  "https://www.google.com/maps/dir/?api=1&destination=Hilton+Garden+Inn,+417+Aviation+Blvd,+Santa+Rosa,+CA+95403";

export const getOct28Copy = (hParam: string | null): LorsCopy => {
  const n = Number(hParam);
  const idx = Number.isInteger(n) && n >= 1 && n <= oct28Headlines.length ? n - 1 : 0;
  const pick = oct28Headlines[idx];
  return { ...baseEn, headline: pick.headline, subhead: pick.subhead } as unknown as LorsCopy;
};
